import { useState } from 'react';

import axios, { AxiosInstance } from 'axios';
import { runInAction } from 'mobx';
import {
  AuthenticationApiFactory,
  CommunityApiFactory,
  Configuration,
  DokumentApiFactory,
  KundeApiFactory,
  LoginCredentialsTypeEnum,
  MieterApiFactory,
  MonitorApiFactory,
  ObjektApiFactory,
  ReservierungApiFactory,
  SchadenApiFactory,
  UserApiFactory,
  WelcomeApiFactory,
  BuchungApiFactory,
  KostenApiFactory,
  MessagesApiFactory,
  TerminApiFactory,
  TopApiFactory,
} from '@codefluegel/wowis-rest-typescript-client';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import { useStore } from './store/context';
import AuthStore from './store/AuthStore';

let accessToken: string;

function getAccessToken(): string {
  return accessToken;
}

axios.interceptors.request.use(request => {
  if (request.headers)
    request.headers.Authorization = `Bearer ${getAccessToken()}`;
  return request;
});

export function createAuthorizedApi<T>(
  baseApiFactory: (
    configuration?: Configuration,
    basePath?: string,
    axiosInstance?: AxiosInstance,
  ) => T,
  authStore: AuthStore,
  baseUrl?: string,
): T {
  const config = new Configuration();
  config.basePath = baseUrl;

  const userData = authStore.getUserData;

  if (userData?.access_token) {
    accessToken = userData.access_token;
    runInAction(() => {
      config.apiKey = `Bearer ${userData?.access_token}`;
    });
  }
  type CreateAuthRefreshInterceptorParameters = Parameters<
    typeof createAuthRefreshInterceptor
  >;

  const refreshAuthLogic: CreateAuthRefreshInterceptorParameters[1] =
    async failedRequest => {
      try {
        const { data: refreshResponse } = await AuthenticationApiFactory(
          config,
          process.env.REACT_APP_API_BASE_URL,
          axios.create(),
        ).getLoginTokens({
          type: LoginCredentialsTypeEnum.Customer,
          grant_type: 'refresh_token',
          refresh_token: authStore.getUserData?.refresh_token,
        });
        if (refreshResponse.access_token) {
          accessToken = refreshResponse.access_token;
        }
        Object.assign(failedRequest.response.config.headers, {
          Authorization: `Bearer ${refreshResponse.access_token}`,
        });

        authStore.setUserData(refreshResponse);
      } catch (e) {
        // log out
        authStore.setUserData({ success: false });
        throw e;
      }
    };
  createAuthRefreshInterceptor(axios, refreshAuthLogic);
  return baseApiFactory(config, process.env.REACT_APP_API_BASE_URL, axios);
}

export default function useClientApi<T>(
  apiFactory: (
    configuration?: Configuration,
    basePath?: string,
    axiosInstance?: AxiosInstance,
  ) => T,
): T {
  const { authStore } = useStore();

  return useState(() => {
    const api = createAuthorizedApi(
      apiFactory,
      authStore,
      process.env.REACT_APP_API_BASE_URL,
    );
    return api;
  })[0];
}

export const useAuthApi: typeof AuthenticationApiFactory = () =>
  useClientApi(AuthenticationApiFactory);

export const useCommunityApi: typeof CommunityApiFactory = () =>
  useClientApi(CommunityApiFactory);

export const useMieterApi: typeof MieterApiFactory = () =>
  useClientApi(MieterApiFactory);

export const useWelcomeApi: typeof WelcomeApiFactory = () =>
  useClientApi(WelcomeApiFactory);

export const useObjectsApi: typeof ObjektApiFactory = () =>
  useClientApi(ObjektApiFactory);

export const useTopApi: typeof TopApiFactory = () =>
  useClientApi(TopApiFactory);

export const useUserApi: typeof UserApiFactory = () =>
  useClientApi(UserApiFactory);

export const useSchadenApi: typeof SchadenApiFactory = () =>
  useClientApi(SchadenApiFactory);

export const useDocumentApi: typeof DokumentApiFactory = () =>
  useClientApi(DokumentApiFactory);

export const useKundenApi: typeof KundeApiFactory = () =>
  useClientApi(KundeApiFactory);

export const useReservationApi: typeof ReservierungApiFactory = () =>
  useClientApi(ReservierungApiFactory);

export const useMonitorApi: typeof MonitorApiFactory = () =>
  useClientApi(MonitorApiFactory);

export const useBuchungApi: typeof BuchungApiFactory = () =>
  useClientApi(BuchungApiFactory);

export const useKostenApi: typeof KostenApiFactory = () =>
  useClientApi(KostenApiFactory);

export const useMessagesApi: typeof MessagesApiFactory = () =>
  useClientApi(MessagesApiFactory);

export const useAppointmentsApi: typeof TerminApiFactory = () =>
  useClientApi(TerminApiFactory);
